export class Tree<T> {
  id: number;
  item: T;
  items: T[];
  children: Tree<T>[];
  selected: boolean;
  mappedNode: { [key: number]: Tree<T> };
  mappedCodeNode: { [key: string]: Tree<T> };
  unfolded: boolean;

  ID: string;
  PARENT_ID: string;

  constructor(item?: T) {
    this.item = item;
    this.selected = false;
    this.children = [];
  }

  create(items: T[], id = 'id', parentId = 'parentId') {
    this.ID = id;
    this.PARENT_ID = parentId;
    this.items = items;

    this.mappedNode = items.reduce((acc, cur, i) => {
      acc[cur[id]] = new Tree<T>(cur);
      return acc;
    }, {});

    this.mappedCodeNode = items.reduce((acc, cur: any, i) => {
      if (cur.code) {
        acc[cur.code] = this.mappedNode[cur[id]];
      }
      return acc;
    }, {});

    Object.keys(this.mappedNode).forEach((idx) => {
      const node = this.mappedNode[idx];
      if (node.item[parentId] && this.mappedNode[node.item[parentId]]) {
        this.mappedNode[node.item[parentId]].children.push(node);
      } else {
        this.children.push(node);
      }
    });
    return this;
  }

  getParent(tree: Tree<T>) {
    return this.mappedNode[tree.item[this.PARENT_ID]];
  }

  private __travelChildren(tree: Tree<T>, selected: boolean) {
    if (tree.children.length === 0) {
      tree.selected = selected;
      return;
    } else {
      tree.selected = selected;
      tree.children.forEach((t) => {
        this.__travelChildren(t, selected);
      });
    }
  }

  private __travelParentByItem(mappedItesm: { [key: number]: T }, item: T) {
    if (mappedItesm[item[this.ID]] || !this.mappedNode[item[this.ID]]) {
      return;
    }

    mappedItesm[item[this.ID]] = this.mappedNode[item[this.ID]].item;
    if (!item[this.PARENT_ID] || !this.mappedNode[item[this.PARENT_ID]]) {
      return;
    } else {
      this.__travelParentByItem(mappedItesm, this.mappedNode[item[this.PARENT_ID]].item);
    }
  }

  public getParentsByItem(item: T) {
    const mappedItems = {};
    this.__travelParentByItem(mappedItems, item);

    const itemsWithParent = Object.keys(mappedItems).map((id) => mappedItems[id]);
    return itemsWithParent.reverse();
  }

  public createSubTree(items: T[]) {
    const mappedItems = {};

    items.forEach((item) => {
      this.__travelParentByItem(mappedItems, item);
    });
    const itemsWithParent = Object.keys(mappedItems).map((id) => mappedItems[id]);
    const tree = new Tree<T>();
    return tree.create(itemsWithParent);
  }

  private __travelParents(node: Tree<T>, checked: boolean) {
    const parent = this.getParent(node);
    if (!parent) {
      return;
    } else {
      if (checked === false) {
        parent.selected = checked;
        if (parent) {
          parent.selected = checked;
        }
      } else {
        const selectedAll = parent.children.every((n) => n.selected === true);
        if (selectedAll) {
          parent.selected = true;
          if (parent) {
            const selectedGrand = parent.children.every((n) => n.selected === true);
            if (selectedGrand) {
              parent.selected = true;
            }
          }
        }
      }
      this.__travelParents(parent, checked);
    }
  }

  selectAllChildren(tree: Tree<T>, selected: boolean) {
    this.__travelChildren(tree, selected);
  }

  getAllChildrenAsCode(tree: Tree<T>) {
    this.__travelChildren(tree, true);
    const codes = Object.keys(this.mappedNode)
      .filter((id) => this.mappedNode[id].selected)
      .map((id) => {
        return this.mappedNode[id].item.code;
      });
    Object.keys(this.mappedNode).forEach((id) => {
      this.mappedNode[id].selected = false;
    });
    return codes;
  }

  getAllChildren(tree: Tree<T>) {
    this.__travelChildren(tree, true);
    const items = Object.keys(this.mappedNode)
      .filter((id) => this.mappedNode[id].selected)
      .map((id) => {
        return this.mappedNode[id].item;
      });
    Object.keys(this.mappedNode).forEach((id) => {
      this.mappedNode[id].selected = false;
    });
    return items;
  }

  selectAllParents(tree: Tree<T>, selected: boolean) {
    this.__travelParents(tree, selected);
  }

  getItemsAsDepth(id: number) {
    let parentId = id;
    const depth = [];
    if (!parentId) {
      return depth;
    }

    while (parentId) {
      const node = this.mappedNode[parentId];
      if (!node) {
        break;
      }
      parentId = node.item[this.PARENT_ID];
      depth.push(node);
    }
    return depth.reverse();
  }

  selectAll(tree: Tree<T>, selected: boolean) {
    this.__travelChildren(tree, selected);
    this.__travelParents(tree, selected);
  }
}
